import { useCallback, useEffect, useMemo } from "react";
import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { useCollectionCachedQuery } from "@/components/localfirst/localfirst-provider";
import { toast } from "sonner";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import type { CollectionView } from "@/convex/companies/collections/kanban_logic";
import { VIEW_META, ViewChromeContext, type ViewChrome } from "@/components/collections/views/view-chrome";
import { getErrorMessage } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/collections/$collectionId")({
  component: CollectionLayout,
});

const VIEW_PATH = {
  table: "/collections/$collectionId/table",
  kanban: "/collections/$collectionId/kanban",
  calendar: "/collections/$collectionId/calendar",
} as const satisfies Record<CollectionView, string>;

/**
 * Vistas de una colección: tabla (`/collections/$id/table`), kanban
 * (`/kanban`) y calendario (`/calendar`). Solo aparecen las habilitadas (las
 * configuradas; la tabla, salvo que se desactive) y en el orden efectivo: el
 * que eligió el usuario (menú junto al selector) o el de Configuración →
 * Colecciones → Vistas. Cada vista pinta el selector compacto al inicio de
 * su barra (con una sola vista no hay selector), así la barra queda para la
 * búsqueda y los filtros. `/collections/$id` abre la primera; una vista deshabilitada
 * redirige a la primera. Cambiar de vista conserva la búsqueda y los filtros.
 */
function CollectionLayout() {
  const { collectionId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const views = useCollectionCachedQuery(api.companies.collections.kanban.getCollectionViews, {
    collectionId: collectionId as Id<"collections">,
  });
  const setMyOrder = useMutation(api.companies.collections.kanban.setMyCollectionViewOrder);
  // Formulario de un registro en página (`/records/…`): sin pestañas de vista.
  const isRecordForm = location.pathname.includes("/records/");
  const current: CollectionView = location.pathname.endsWith("/kanban")
    ? "kanban"
    : location.pathname.endsWith("/calendar")
      ? "calendar"
      : "table";

  const openFirst = useCallback(async (view: CollectionView | null) => {
    try {
      const order = view ? [view, ...(views?.views ?? []).filter((v) => v !== view)] : null;
      await setMyOrder({ collectionId: collectionId as Id<"collections">, viewOrder: order });
      toast.success(view ? `Se abrirá en ${VIEW_META[view].label}` : "Se usa el orden de la colección");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [setMyOrder, collectionId, views?.views]);

  const goTo = useCallback(
    (view: CollectionView, replace = false) => {
      void navigate({
        to: VIEW_PATH[view],
        params: { collectionId },
        search: ((prev: Record<string, unknown>) => {
          // La página es de la tabla y el modo/fecha del calendario: no viajan.
          const { page: _page, mode: _mode, date: _date, ...rest } = prev ?? {};
          return rest;
        }) as never,
        replace,
      });
    },
    [navigate, collectionId],
  );
  const onChange = useCallback((view: CollectionView) => goTo(view), [goTo]);

  // Una vista deshabilitada (p. ej. la tabla desactivada) abre la primera.
  const enabled = views?.views;
  useEffect(() => {
    if (isRecordForm) return;
    if (enabled && enabled.length > 0 && !enabled.includes(current)) goTo(enabled[0], true);
  }, [enabled, current, goTo, isRecordForm]);

  const isConfig = !!views?.config;
  useEffect(() => {
    if (isConfig) {
      void navigate({ to: "/settings/collections/config/$collectionId", params: { collectionId }, replace: true });
    }
  }, [isConfig, navigate, collectionId]);

  const chrome = useMemo<ViewChrome>(
    () => ({
      views: views?.views ?? [],
      current,
      onChange,
      preferred: views?.views[0],
      personal: !!views?.personal,
      openFirst: (view) => void openFirst(view),
    }),
    [views, current, onChange, openFirst],
  );

  // De configuración: sin vistas; su registro se edita en Configuración.
  if (views?.config) return null;
  if (isRecordForm) return <Outlet />;

  return (
    <ViewChromeContext.Provider value={chrome}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Outlet />
      </div>
    </ViewChromeContext.Provider>
  );
}
